import { Cloud, Lock, Database, Zap, Shield, Code } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const TechnologySection = () => {
  const techStack = [
    { name: 'React', icon: '⚛️', color: 'text-cyan-600' },
    { name: 'NestJS', icon: '🐈', color: 'text-red-600' },
    { name: 'PostgreSQL', icon: '🐘', color: 'text-blue-600' },
    { name: 'Docker', icon: '🐳', color: 'text-blue-500' },
    { name: 'AWS', icon: '☁️', color: 'text-orange-600' },
  ];

  const securityFeatures = [
    {
      icon: Cloud,
      title: 'Cloud-Native Architecture',
      description: 'Scalable infrastructure that grows with your workforce. Access from anywhere, anytime.',
    },
    {
      icon: Lock,
      title: 'Role-Based Access',
      description: 'Granular permissions ensure people see only what they need. Security without friction.',
    },
    {
      icon: Database,
      title: 'Data Isolation',
      description: 'Your workplace data is completely separate and secure. Multi-tenant architecture with single-tenant privacy.',
    },
    {
      icon: Zap,
      title: 'Secure Integrations',
      description: 'Connect with your existing tools safely. API-first design for seamless workplace connectivity.',
    },
    {
      icon: Shield,
      title: 'AES-256 Encryption',
      description: 'Military-grade encryption for all workplace data; at rest and in transit.',
    },
    {
      icon: Code,
      title: 'Continuous Security',
      description: 'Regular audits and updates keep your workplace data protected and compliant.',
    },
  ];

  return (
    <section className="py-8 sm:py-10 md:py-12 bg-gradient-subtle">
      <div className="max-w-[745px] w-full mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10 animate-fade-in">
          <Badge className="mb-3 bg-primary/10 text-primary border-primary/20 text-xs">
            Technology & Security
          </Badge>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            <span className="emphasize">Enterprise-Grade</span> Workplace Infrastructure
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            Built on secure, modern infrastructure that IT teams trust. Your workplace data is always <span className="emphasize-accent font-semibold text-foreground">protected, accessible, and under your control</span>.
          </p>
        </div>

        {/* Security Features Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-3 sm:gap-4 mb-8 sm:mb-10">
          {securityFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className="p-3 sm:p-4 hover-lift bg-card border-border/50 hover:border-primary/30 transition-all animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-2 sm:mb-3">
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <h3 className="text-xs sm:text-sm font-bold mb-1">{feature.title}</h3>
                <p className="text-[10px] sm:text-xs text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </Card>
            );
          })}
        </div>

        {/* Tech Stack */}
        <div className="relative">
          {/* Background layer */}
          <div className="absolute inset-0 bg-dark-section rounded-lg translate-x-0.5 translate-y-0.5"></div>
          
          {/* Main card */}
          <Card className="relative p-5 sm:p-6 md:p-8 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-teal-500/10 border border-white/10 animate-fade-in">
            <div className="text-center mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-bold mb-1.5 sm:mb-2 text-white">Built With Modern Technology</h3>
              <p className="text-xs text-gray-300">
                Leveraging industry-leading tools and frameworks
              </p>
            </div>

          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {techStack.map((tech, index) => (
              <div
                key={index}
                className="flex flex-col items-center space-y-1.5 p-2.5 sm:p-3 rounded-lg bg-background hover:bg-muted/50 transition-colors group w-[110px] sm:w-[120px]"
              >
                <div className="text-2xl sm:text-3xl group-hover:scale-110 transition-transform">
                  {tech.icon}
                </div>
                <span className={`text-xs font-semibold ${tech.color}`}>
                  {tech.name}
                </span>
              </div>
            ))}
          </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default TechnologySection;
